<template>
  <div class="text-left">
    <RejectCommentLayout :comment-text="reject_comment" />
    <v-card>
      <v-form class="pa-6" ref="form">
        <h5 v-if="listAddServices.length > 0">
          {{ $t("form.addServices") }}
        </h5>
        <v-alert
          v-if="exclusiveConflictMessage"
          dense
          outlined
          type="error"
          class="mb-4"
        >
          {{ exclusiveConflictMessage }}
        </v-alert>
        <div v-if="sortedAddServices.length > 0">
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="4"
              lg="3"
              :key="service.code"
              class="service-option"
              v-for="service in sortedAddServices"
            >
                <v-checkbox
                  multiple
                  v-model="investigator.account.addServices"
                  :value="service.id"
                  :readonly="disabledForm || !isServiceAvailableInForms(service)"
                  class="service-checkbox"
                  @change="normalizeConflictingSelections"
                >
                  <template v-slot:label>
                    <div>
                      <div class="service-label-row">
                        <span
                          :class="{
                            'service-label-name': true,
                            'service-label-disabled': isServiceStriked(service)
                          }"
                        >
                          {{ service.name }}
                        </span>
                        <v-tooltip v-if="service.restricted" top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="ml-2"
                              color="red"
                              small
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-lock
                            </v-icon>
                          </template>
                          {{ $t("form.service_restricted_tooltip") }}
                        </v-tooltip>
                        <v-tooltip v-if="service.requires_agreement" top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="ml-2"
                              color="blue"
                              small
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-file-sign
                            </v-icon>
                          </template>
                          {{ $t("form.service_requires_agreement_tooltip") }}
                        </v-tooltip>
                        <v-tooltip
                          v-if="service.exclusive_with && service.exclusive_with.length"
                          top
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="ml-2"
                              color="orange"
                              small
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-call-split
                            </v-icon>
                          </template>
                          {{ exclusiveServicesLabel(service) }}
                        </v-tooltip>
                        <v-btn
                          icon
                          small
                          color="primary"
                          class="service-info-button"
                          @click.stop="openServiceInfo(service)"
                        >
                          <v-icon small>mdi-information-outline</v-icon>
                        </v-btn>
                      </div>
                      <div
                        v-if="service.contact_people && service.contact_people.length"
                        class="grey--text text--darken-1 caption"
                      >
                        {{ contactPeopleLabel(service.contact_people) }}
                      </div>
                      <div
                        v-if="!isServiceAvailableInForms(service)"
                        class="service-unavailable-hint"
                      >
                        {{ $t("form.service_unavailable_in_forms_hint") }}
                      </div>
                    </div>
                  </template>
                </v-checkbox>
            </v-col>
          </v-row>
        </div>

        <h5 v-if="sortedRemoveServices.length > 0">
          {{ $t("form.removeServices") }}
        </h5>
        <div v-if="sortedRemoveServices.length > 0">
          <v-row>
            <v-col
              cols="12"
              sm="6"
              md="4"
              lg="3"
              :key="service.code"
              class="service-option"
              v-for="service in sortedRemoveServices"
            >
                <v-checkbox
                  multiple
                  v-model="investigator.account.removeServices"
                  :value="service.id"
                  :readonly="disabledForm"
                  class="service-checkbox"
                >
                  <template v-slot:label>
                    <div>
                      <div class="service-label-row">
                        <span class="service-label-name">{{ service.name }}</span>
                        <v-tooltip v-if="service.restricted" top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="ml-2"
                              color="red"
                              small
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-lock
                            </v-icon>
                          </template>
                          {{ $t("form.service_restricted_tooltip") }}
                        </v-tooltip>
                        <v-tooltip v-if="service.requires_agreement" top>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="ml-2"
                              color="blue"
                              small
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-file-sign
                            </v-icon>
                          </template>
                          {{ $t("form.service_requires_agreement_tooltip") }}
                        </v-tooltip>
                        <v-tooltip
                          v-if="service.exclusive_with && service.exclusive_with.length"
                          top
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              class="ml-2"
                              color="orange"
                              small
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-call-split
                            </v-icon>
                          </template>
                          {{ exclusiveServicesLabel(service) }}
                        </v-tooltip>
                        <v-btn
                          icon
                          small
                          color="primary"
                          class="service-info-button"
                          @click.stop="openServiceInfo(service)"
                        >
                          <v-icon small>mdi-information-outline</v-icon>
                        </v-btn>
                      </div>
                      <div
                        v-if="service.contact_people && service.contact_people.length"
                        class="grey--text text--darken-1 caption"
                      >
                        {{ contactPeopleLabel(service.contact_people) }}
                      </div>
                    </div>
                  </template>
                </v-checkbox>
            </v-col>
          </v-row>
        </div>
        <v-divider class="form-actions-divider" />
        <UserFormActions
          form_name_text="Modify services"
          :form_name="form_name"
          :refForm="$refs.form"
          :object_type="object_type"
          :data="processData"
          :state="state"
          :send-disabled="Boolean(exclusiveConflictMessage)"
          @change-state="changeState"
          class="text-center mt-6"
        />
      </v-form>
    </v-card>
    <v-dialog v-model="serviceInfoDialog" max-width="600px">
      <v-card v-if="selectedService">
        <v-card-title class="headline">
          {{ selectedService.name }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <strong>{{ $t("form.description") }}:</strong>
            <div
              v-if="getServiceDescription(selectedService)"
              v-html="getServiceDescription(selectedService)"
            />
            <div v-else>
              {{ $t("table.features.missingDescription") }}
            </div>
          </div>
          <div class="mb-2">
            <strong>{{ $t("form.restricted") }}:</strong>
            <span>
              {{
                selectedService.restricted
                  ? $t("table.features.restricted")
                  : $t("table.features.notRestricted")
              }}
            </span>
          </div>
          <div class="mb-2">
            <strong>{{ $t("form.requires_agreement") }}:</strong>
            <span>
              {{
                selectedService.requires_agreement
                  ? $t("table.features.requiresAgreement")
                  : $t("table.features.notRequiresAgreement")
              }}
            </span>
          </div>
          <div>
            <strong>{{ $t("form.documentation_link") }}:</strong>
            <span v-if="selectedService.documentation_link">
              <a
                :href="selectedService.documentation_link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ selectedService.documentation_link }}
              </a>
            </span>
            <span v-else>-</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeServiceInfo">
            {{ $t("button.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { normalizeServices } from "../../../utils/service";
import UserFormActions from "../../action_buttons/UserFormActions";
import RejectCommentLayout from "../../layouts/RejectCommentLayout";
import changeState from "../../../mixins/changeState";
import investigatorData from "../../../mixins/investigator_data";
import serviceDisplay from "../../../mixins/serviceDisplay";
export default {
  mixins: [changeState, investigatorData, serviceDisplay],
  components: { UserFormActions, RejectCommentLayout },
  name: "ModifyServicesForm",

  computed: {
    updateForm() {
      return this.$route.meta.updateForm;
    },
    disabledForm() {
      let states = ["VD", "PA", "PV", "PC"];
      return states.includes(this.state);
    },
    allServices() {
      return [...this.listAddServices, ...this.listRemoveServices];
    },
    sortedAddServices() {
      return this.sortServices(this.listAddServices);
    },
    sortedRemoveServices() {
      return this.sortServices(this.listRemoveServices);
    },
    activeServiceIds() {
      const current = Array.isArray(this.investigator.account.services)
        ? this.investigator.account.services.slice()
        : [];
      const remove = Array.isArray(this.investigator.account.removeServices)
        ? this.investigator.account.removeServices
        : [];
      return current.filter(id => !remove.includes(id));
    },
    exclusiveConflictPairs() {
      const pairs = [];
      const addServices = Array.isArray(this.investigator.account.addServices)
        ? this.investigator.account.addServices
        : [];

      addServices.forEach((serviceId, index) => {
        addServices.slice(index + 1).forEach(otherId => {
          if (this.servicesConflict(serviceId, otherId)) {
            pairs.push(this.formatConflictPair(serviceId, otherId));
          }
        });
        this.activeServiceIds.forEach(activeId => {
          if (this.servicesConflict(serviceId, activeId)) {
            pairs.push(this.formatConflictPair(serviceId, activeId));
          }
        });
      });

      return [...new Set(pairs.filter(Boolean))];
    },
    exclusiveConflictMessage() {
      if (this.exclusiveConflictPairs.length === 0) {
        return "";
      }
      return this.$t("form.validation.serviceExclusiveConflict", [
        this.exclusiveConflictPairs.join("; ")
      ]);
    },
    processData() {
      return {
        title: this.investigator.name,
        object_type: this.object_type,
        form_name: this.form_name,
        object_id: this.investigator.id,
        data: {
          user: {
            account: {
              addServices: this.investigator.account.addServices,
              removeServices: this.investigator.account.removeServices
            }
          }
        }
      };
    }
  },

  created() {
    this.getData();
  },

  data: () => ({
    menu: false,
    object_type: "investigator",
    form_name: "modifyServicesForm",
    state: "",
    reject_comment: "",
    object_id: null,
    listAddServices: [],
    listRemoveServices: [],
    serviceInfoDialog: false,
    selectedService: null,
    personNameIndex: {}
  }),

  methods: {
    getData() {
      let path = "";
      if (this.updateForm || this.$route.meta.readOnly) {
        path =
          this.$store.state.endpoint +
          "/user/moderatedOperations/investigator/" +
          this.$route.params.id;
        axios
          .get(path)
          .then(response => {
            this.investigator.account = response.data.data.user.account;
            this.state = response.data.state;
            this.form_name = response.data.form_name;
            this.reject_comment = response.data.reject_comment;
            this.investigator.id = response.data.object_id;
            this.getInvestigatorName(
              this.$store.state.endpoint + "/person/" + this.investigator.id
            );
            this.getInvestigatorServices(this.investigator.id);
          })
          .catch(error => {
            console.log(error);
          });
      } else {
        this.getInvestigatorName(
          this.$store.state.endpoint + "/person/" + this.$route.params.id
        );
        this.getInvestigatorServices(this.$route.params.id);
      }
    },

    getInvestigatorServices(id) {
      let path = this.$store.state.endpoint + "/account/" + id;

      axios
        .get(path)
        .then(response => {
          if (this.state === "PC" || this.state === "RJ") {
            response.data.services.forEach(service => {
              if (!this.investigator.account.addServices.includes(service)) {
                this.investigator.account.services.push(service);
              }
              this.investigator.account.removeServices.forEach(service => {
                this.investigator.account.services.push(service);
              });
            });
          } else {
            this.investigator.account.services = response.data.services;
          }
        })
        .finally(this.getServices)
        .catch(error => {
          console.log(error);
        });
    },

    getServices() {
      let path = this.$store.state.endpoint + "/service/no-basic";
      axios
        .get(path)
        .then(response => {
          normalizeServices(response.data).forEach(service => {
            if (service.hidden === true) {
              return;
            }
            if (this.investigator.account.services.includes(service.id)) {
              this.listRemoveServices.push(service);
            } else {
              this.listAddServices.push(service);
            }
          });
          this.normalizeConflictingSelections();
          this.getPersonNames();
        })
        .catch(error => {
          console.log(error);
        });
    },
    getPersonNames() {
      axios
        .get(this.$store.state.endpoint + "/personName")
        .then(response => {
          const index = {};
          response.data.forEach(person => {
            if (person && person.preferred_name) {
              index[person.id] =
                person.preferred_name.given_name +
                " " +
                person.preferred_name.family_name;
            }
          });
          this.personNameIndex = index;
        })
        .catch(error => {
          console.log(error);
        });
    },
    openServiceInfo(service) {
      this.selectedService = service;
      this.serviceInfoDialog = true;
    },
    closeServiceInfo() {
      this.serviceInfoDialog = false;
    },
    getServiceDescription(service) {
      if (!service || !service.description) {
        return "";
      }
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "es";
      if (service.description[locale]) {
        return service.description[locale];
      }
      return (
        service.description.es ||
        service.description.en ||
        service.description.gl ||
        ""
      );
    },
    getServiceById(id) {
      return this.allServices.find(service => service && service.id === id) || null;
    },
    sortServices(services) {
      if (!Array.isArray(services)) {
        return [];
      }
      return services.slice().sort((a, b) => {
        const orderDiff = (a.order || 0) - (b.order || 0);
        if (orderDiff !== 0) {
          return orderDiff;
        }
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    },
    getExclusiveIds(service) {
      if (!service || !Array.isArray(service.exclusive_with)) {
        return [];
      }
      return service.exclusive_with
        .map(item => (typeof item === "object" ? item.id : item))
        .filter(item => item !== undefined && item !== null);
    },
    servicesConflict(firstId, secondId) {
      if (
        firstId === undefined ||
        firstId === null ||
        secondId === undefined ||
        secondId === null ||
        firstId === secondId
      ) {
        return false;
      }
      const firstService = this.getServiceById(firstId);
      const secondService = this.getServiceById(secondId);
      const firstExclusive = this.getExclusiveIds(firstService);
      const secondExclusive = this.getExclusiveIds(secondService);
      return (
        firstExclusive.includes(secondId) || secondExclusive.includes(firstId)
      );
    },
    formatConflictPair(firstId, secondId) {
      const firstName = this.getExclusiveServiceName(firstId);
      const secondName = this.getExclusiveServiceName(secondId);
      if (!firstName || !secondName) {
        return "";
      }
      return firstName + " / " + secondName;
    },
    getExclusiveServiceName(service) {
      if (!service) {
        return "";
      }
      if (typeof service === "object") {
        if (service.name) {
          return service.name;
        }
        if (service.id !== undefined && service.id !== null) {
          const found = this.getServiceById(service.id);
          return found && found.name ? found.name : String(service.id);
        }
        return "";
      }
      const found = this.getServiceById(service);
      return found && found.name ? found.name : String(service);
    },
    exclusiveServicesLabel(service) {
      const names = this.getExclusiveIds(service)
        .map(item => this.getExclusiveServiceName(item))
        .filter(name => name);
      if (names.length === 0) {
        return this.$t("form.exclusive_with");
      }
      return this.$t("form.exclusive_with") + ": " + names.join(", ");
    },
    isServiceStriked(service) {
      if (!service || this.investigator.account.addServices.includes(service.id)) {
        return false;
      }
      const selectedAdd = Array.isArray(this.investigator.account.addServices)
        ? this.investigator.account.addServices
        : [];
      return [...selectedAdd, ...this.activeServiceIds].some(
        serviceId => serviceId !== service.id && this.servicesConflict(service.id, serviceId)
      );
    },
    normalizeConflictingSelections() {
      if (!Array.isArray(this.investigator.account.addServices)) {
        this.investigator.account.addServices = [];
      }
      this.investigator.account.addServices = this.filterUnavailableFormServices(
        this.investigator.account.addServices,
        this.listAddServices
      );
    },
    contactPeopleLabel(people) {
      if (!Array.isArray(people)) {
        return "";
      }
      return people
        .map(person => this.getContactPersonName(person))
        .filter(name => name)
        .join(", ");
    },
    getContactPersonName(person) {
      if (!person) {
        return "";
      }
      if (typeof person === "number") {
        return this.personNameIndex[person] || String(person);
      }
      if (typeof person === "string") {
        return this.personNameIndex[person] || person;
      }
      if (person.preferred_name && person.preferred_name.given_name) {
        return (
          person.preferred_name.given_name +
          (person.preferred_name.family_name ? " " + person.preferred_name.family_name : "")
        );
      }
      return person.name || "";
    }
  }
};
</script>

<style scoped>
.service-option {
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
}

.service-label-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.service-label-name {
  line-height: 1.3;
}

.service-label-disabled {
  text-decoration: line-through;
  opacity: 0.7;
}

.service-info-button {
  margin-left: 0.1rem;
}

.service-unavailable-hint {
  color: #8d6e63;
  font-size: 0.75rem;
  line-height: 1.3;
}

.form-actions-divider {
  margin-top: 0.75rem;
}

::v-deep .service-checkbox {
  margin-top: 0.1rem;
  padding-top: 0.1rem;
}

::v-deep .service-checkbox .v-input__slot {
  margin-bottom: 0.15rem;
}

::v-deep .service-checkbox .v-messages {
  min-height: 0;
}
</style>
